import { Injectable, Optional } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take, map, distinctUntilChanged, filter } from 'rxjs/operators';
import { DataBindingService } from './data-binding.service';
import { MasterDetailService } from './master-detail.service';
import { RxTimingService } from './rx-timing.service';

/**
 * Interface para representar uma seleção (item, índice e path do array)
 */
export interface SelectionItem {
  item: any | null;
  index: number;
  arrayPath: string;
}

/**
 * Interface unificada para o estado de seleção completo
 * Consolida mainSelection e currentSelection em uma única estrutura
 */
export interface SelectionState {
  mainSelection: SelectionItem;
  currentSelection: SelectionItem;
  targetPanelIds: string[];
  sourceGridId: string;
}

/**
 * Service de Sincronização de Dados
 * 
 * Responsável por coordenar mudanças entre:
 * - Grids (edição inline)
 * - Formulários (campos de input)
 * - JSON de dados (source of truth)
 * 
 * Fluxo de Sincronização:
 * 1. Usuário edita em Grid → Atualiza JSON → Notifica Formulário
 * 2. Usuário edita em Formulário → Atualiza JSON → Notifica Grid
 * 3. JSON é sempre a fonte única da verdade
 * 
 * IMPORTANTE: Este serviço NÃO é global (providedIn: 'root')
 * Ele deve ser fornecido a nível de componente junto com DataBindingService
 * para garantir instâncias isoladas por launcher.
 */
@Injectable()
export class DataSyncService {
  
  // Seleção da grid PRINCIPAL (nível 1: response.item_list)
  // Esta NUNCA é sobrescrita por grids aninhadas - serve como contexto para elas
  public mainSelectionSubject = new BehaviorSubject<{
    item: any | null;
    index: number;
    arrayPath: string;
  }>({
    item: null,
    index: -1,
    arrayPath: ''
  });
  
  // Rastrea o item atualmente selecionado e seu índice (pode ser nível 1 OU 2+)
  public currentSelectionSubject = new BehaviorSubject<{
    item: any | null;
    index: number;
    arrayPath: string;
    targetNodeIds?: Set<string>;
  }>({
    item: null,
    index: -1,
    arrayPath: ''
  });
  
  public currentSelection$ = this.currentSelectionSubject.asObservable();

  /**
   * CORREÇÃO: Subject para sincronização de campos com mesmo dataPath
   * Quando um campo é editado, emite o dataPath e valor para que outros campos
   * com o mesmo dataPath possam se atualizar
   * 
   * targetNodeIds: Se fornecido, APENAS campos com nodeId no Set receberão a atualização.
   * Usado por fillNestedFormDirectly para evitar que campos da grade principal sejam afetados
   * quando uma aba filha tem campos com mesmo dataPath.
   */
  private fieldSyncSubject = new Subject<{ dataPath: string; value: any; sourceNodeId: string; targetNodeIds?: Set<string> }>();
  
  /**
   * Observable para campos se inscreverem e receberem atualizações de campos duplicados
   */
  public fieldSync$ = this.fieldSyncSubject.asObservable();
  
  /**
   * Emite uma atualização de campo para sincronização de campos duplicados
   * @param targetNodeIds Se fornecido, apenas campos com nodeId no Set receberão a atualização
   */
  public emitFieldSync(dataPath: string, value: any, sourceNodeId: string, targetNodeIds?: Set<string>): void {
    this.fieldSyncSubject.next({ dataPath, value, sourceNodeId, targetNodeIds });
  }

  /**
   * FASE 3: Observable derivado para seleção principal
   * Usa distinctUntilChanged para evitar emissões duplicadas quando apenas o item muda
   * mas index e arrayPath permanecem iguais
   */
  public mainSelection$: Observable<SelectionItem> = this.mainSelectionSubject.asObservable().pipe(
    distinctUntilChanged((a, b) => 
      a.index === b.index && a.arrayPath === b.arrayPath
    )
  );

  /**
   * FASE 3: Observable derivado para seleção atual (pode ser grade principal ou aninhada)
   * Emite apenas quando index ou arrayPath mudam (ignora mudanças apenas no item)
   */
  public currentSelectionDistinct$: Observable<SelectionItem> = this.currentSelectionSubject.asObservable().pipe(
    distinctUntilChanged((a, b) => 
      a.index === b.index && a.arrayPath === b.arrayPath
    )
  );

  // CORREÇÃO: Map por arrayPath para isolar flags por instância de grid (evita loops entre grids diferentes)
  private _updatingFromFormPaths = new Map<string, number>();

  /**
   * Flag que indica que uma célula da grade está sendo editada.
   * Grids filhas devem ignorar emissões de mainSelectionSubject durante edição de célula,
   * pois a emissão é apenas para atualizar o item (referência), não para mudar de linha.
   */
  private _isCellEditing = false;
  public get isCellEditing(): boolean { return this._isCellEditing; }

  /**
   * Marca grades que receberam auto-add inicial (canvas) para que o DataGrid
   * abra automaticamente o formulário no primeiro carregamento.
   */
  private pendingInitialAutoAddGridIds = new Set<string>();
  
  /**
   * CORREÇÃO: Define a flag de edição de célula
   * Usado pelos eventos focus/blur dos inputs da grade
   */
  public setCellEditing(value: boolean): void {
    this._isCellEditing = value;
  }

  /**
   * Verifica se um path específico está atualizando do formulário
   * @param arrayPath Path do array (ex: 'response.item_list' ou 'response.item_list.channels')
   */
  public isUpdatingFromFormForPath(arrayPath: string): boolean {
    const timestamp = this._updatingFromFormPaths.get(arrayPath);
    if (!timestamp) return false;
    return (Date.now() - timestamp) < 300; // 300ms de janela
  }

  /**
   * Getter para compatibilidade: verifica se QUALQUER path está atualizando
   */
  public get isUpdatingFromForm(): boolean {
    const now = Date.now();
    for (const [, timestamp] of this._updatingFromFormPaths) {
      if ((now - timestamp) < 300) return true;
    }
    return false;
  }

  /** Evita gravar o mesmo valor na linha errada quando a seleção muda logo após a edição; também suprime duplicatas (re-fill dispara valueChanges). */
  private lastFormWrite: { value: any; effPath: string; effIdx: number; fieldPath: string; time: number } | null = null;
  private static readonly LAST_WRITE_SUPPRESS_MS = 400;
  
  constructor(
    private dataBindingService: DataBindingService,
    private masterDetailService: MasterDetailService,
    @Optional() private rxTiming: RxTimingService
  ) {
    this.initializeMasterDetailListener();
  }

  // ============================================================================
  // FASE 3: Métodos auxiliares para manipulação unificada do estado de seleção
  // ============================================================================

  /**
   * Obtém o estado de seleção atual completo
   * @returns SelectionState com mainSelection e currentSelection
   */
  getSelectionState(): SelectionState {
    return {
      mainSelection: this.mainSelectionSubject.value,
      currentSelection: this.currentSelectionSubject.value,
      targetPanelIds: [],
      sourceGridId: ''
    };
  }

  /**
   * Atualiza a seleção principal (grade de nível 1)
   * @param selection Objeto com item, index e arrayPath
   */
  setMainSelection(selection: SelectionItem): void {
    this.mainSelectionSubject.next(selection);
  }

  /**
   * Atualiza a seleção atual (pode ser nível 1 ou 2+)
   * @param selection Objeto com item, index e arrayPath
   */
  setCurrentSelection(selection: SelectionItem): void {
    this.currentSelectionSubject.next(selection);
  }

  /**
   * Limpa ambas as seleções (útil ao trocar de tela ou resetar)
   */
  clearSelections(): void {
    const emptySelection: SelectionItem = {
      item: null,
      index: -1,
      arrayPath: ''
    };
    this.mainSelectionSubject.next(emptySelection);
    this.currentSelectionSubject.next(emptySelection);
  }

  /**
   * Verifica se a seleção atual é de uma grade aninhada
   * @returns true se o arrayPath tem mais de 2 partes (ex: response.magalu.channels)
   */
  isNestedSelection(): boolean {
    const path = this.currentSelectionSubject.value.arrayPath;
    return path.split('.').length > 2;
  }

  /**
   * Obtém o item selecionado na grade principal
   * @returns O item selecionado ou null
   */
  getMainSelectedItem(): any | null {
    return this.mainSelectionSubject.value.item;
  }

  /**
   * Obtém o item selecionado atualmente (pode ser de qualquer nível)
   * @returns O item selecionado ou null
   */
  getCurrentSelectedItem(): any | null {
    return this.currentSelectionSubject.value.item;
  }
  
  /**
   * Escuta mudanças no Master-Detail para rastrear seleção atual
   */
  private initializeMasterDetailListener(): void {
    this.masterDetailService.selectedRow$.subscribe(state => {
      if (state.selectedRow && state.arrayPath) {
        const current = this.mainSelectionSubject.value;
        
        // CORREÇÃO: Se já temos uma seleção válida para o mesmo arrayPath e índice,
        // não sobrescrever (evita race conditions com autoSelectFirstRow)
        // IMPORTANTE: state.rowIndex pode ser undefined, então verificar explicitamente
        if (current.arrayPath === state.arrayPath && 
            state.rowIndex !== undefined &&
            current.index === state.rowIndex && 
            current.index >= 0) {
          return;
        }
        
        this.updateCurrentSelection(state.selectedRow, state.arrayPath, state.rowIndex);
      }
    });
  }
  
  /**
   * Atualiza a seleção atual (item e seu índice)
   * @param item Item selecionado
   * @param arrayPath Path do array (ex: 'response.item_list' ou 'response.item_list.image.image_id_list')
   * @param rowIndex Índice da linha no array (0-based) - se fornecido, evita busca por JSON.stringify
   */
  private updateCurrentSelection(item: any, arrayPath: string, rowIndex?: number): void {
    const data = this.dataBindingService.getCurrentData();
    if (!data) return;
    
    const pathParts = arrayPath.split('.');
    
    if (pathParts.length > 2) {
      const parentSelection = this.mainSelectionSubject.value;
      
      if (parentSelection.index === -1 || !parentSelection.arrayPath) {
        return;
      }
      
      // Construir path com índice do pai: response.item_list[0].promotion_image.image_id_list
      const parentArrayPath = parentSelection.arrayPath; // ex: "response.item_list"
      
      // Extrair o path aninhado removendo o prefixo do pai
      // arrayPath: "response.item_list.promotion_image.image_id_list"
      // parentArrayPath: "response.item_list"
      // nestedPath: "promotion_image.image_id_list"
      let nestedPath = arrayPath;
      if (arrayPath.startsWith(parentArrayPath + '.')) {
        nestedPath = arrayPath.substring(parentArrayPath.length + 1);
      }
      
      const fullPath = `${parentArrayPath}[${parentSelection.index}].${nestedPath}`;
      
      // Resolver array usando path completo
      const targetArray = this.dataBindingService.resolvePath(data, fullPath);
      
      if (Array.isArray(targetArray)) {
        // Usar rowIndex se fornecido, senão buscar
        let index: number;
        if (rowIndex !== undefined && rowIndex >= 0 && rowIndex < targetArray.length) {
          index = rowIndex; // Usar índice fornecido (mais confiável)
        } else {
          // Fallback: buscar usando JSON.stringify (menos confiável)
          index = targetArray.findIndex(i => 
            JSON.stringify(i) === JSON.stringify(item)
          );
        }
        
        if (index !== -1) {
          // Guardar arrayPath sem índice (ex: response.item_list.channels) para bater com path usado na grade
          this.currentSelectionSubject.next({
            item: targetArray[index],
            index,
            arrayPath
          });
        }
      }
    } else {
      // Grid PRINCIPAL (nível 1: response.item_list) - usar path diretamente
      const targetArray = this.dataBindingService.resolvePath(data, arrayPath);
      
      if (Array.isArray(targetArray)) {
        // Usar rowIndex se fornecido, senão buscar
        let index: number;
        if (rowIndex !== undefined && rowIndex >= 0 && rowIndex < targetArray.length) {
          index = rowIndex; // Usar índice fornecido (mais confiável)
        } else {
          // Fallback: buscar usando JSON.stringify (menos confiável)
          index = targetArray.findIndex(i => 
            JSON.stringify(i) === JSON.stringify(item)
          );
        }
        
        if (index !== -1) {
          // CORREÇÃO: Verificar se mainSelectionSubject já tem o mesmo índice
          // para evitar emissões duplicadas
          const current = this.mainSelectionSubject.value;
          const needsMainEmission = current.arrayPath !== arrayPath || current.index !== index;
          
          // SALVAR em AMBOS: mainSelection E currentSelection
          const selection = { 
            item: targetArray[index],
            index, 
            arrayPath 
          };
          
          // Só emitir mainSelectionSubject se realmente mudou
          if (needsMainEmission) {
            this.mainSelectionSubject.next(selection);
          }
          this.currentSelectionSubject.next(selection);
        }
      }
    }
  }
  
  /**
   * ⭐ Atualiza um campo do item atualmente selecionado
   * @param fieldPath Path do campo relativo ao item (ex: "item_name", "brand.brand_id")
   * @param value Novo valor
   * @param formContextArrayPath Path do array da grade a que o formulário pertence (ex: "response.item_list" = grade pai). Se informado, define se a edição é na grade principal ou filha e evita usar currentSelection quando o usuário está editando o formulário da grade pai.
   */
  updateCurrentItemField(fieldPath: string, value: any, formContextArrayPath?: string): void {
    const selection = this.currentSelectionSubject.value;
    const mainSelection = this.mainSelectionSubject.value;

    // CORREÇÃO: Validar se a seleção está correta antes de escrever
    // Se o formContextArrayPath coincide com mainSelection, usar mainSelection
    // Isso evita escrever na linha errada quando a seleção mudou recentemente

    // Decidir se a edição é na grade PRINCIPAL ou na grade FILHA com base no contexto do formulário
    let isNestedGrid: boolean;
    let effectiveArrayPath: string;
    let effectiveIndex: number;
    let effectiveItem: any;
    let branch = '';

    if (formContextArrayPath != null && formContextArrayPath !== '') {
      // Contexto explícito: decidir se a edição é na grade PAI ou FILHA
      if (formContextArrayPath === mainSelection.arrayPath) {
        branch = 'PAI';
        isNestedGrid = false;
        effectiveArrayPath = mainSelection.arrayPath;
        effectiveIndex = mainSelection.index;
        effectiveItem = mainSelection.item;

        // CORREÇÃO: Validar índice antes de continuar
        if (effectiveIndex < 0) {
          return;
        }
      } else if (formContextArrayPath.startsWith(mainSelection.arrayPath + '.')) {
        const selectionMatchesNestedContext = selection.arrayPath === formContextArrayPath && selection.index >= 0;
        if (selectionMatchesNestedContext) {
          branch = 'FILHA_MATCH';
          isNestedGrid = true;
          effectiveArrayPath = selection.arrayPath;
          effectiveIndex = selection.index;
          effectiveItem = selection.item;
        } else {
          const nestedPath = formContextArrayPath.replace(mainSelection.arrayPath + '.', '');
          const parentItem = mainSelection.item;
          const nestedArray = parentItem
            ? this.dataBindingService.resolvePath(parentItem, nestedPath)
            : null;
          if (Array.isArray(nestedArray) && nestedArray.length > 0) {
            branch = 'FILHA_FALLBACK_0';
            isNestedGrid = true;
            effectiveArrayPath = formContextArrayPath;
            effectiveIndex = 0;
            effectiveItem = nestedArray[0];
          } else {
            branch = 'FILHA_EMPTY→PAI';
            isNestedGrid = false;
            effectiveArrayPath = mainSelection.arrayPath;
            effectiveIndex = mainSelection.index;
            effectiveItem = mainSelection.item;
          }
        }
      } else {
        branch = 'LEGACY_SEL';
        isNestedGrid = selection.arrayPath !== mainSelection.arrayPath &&
          selection.arrayPath.startsWith(mainSelection.arrayPath + '.');
        effectiveArrayPath = selection.arrayPath;
        effectiveIndex = selection.index;
        effectiveItem = selection.item;
      }
    } else {
      branch = 'LEGACY';
      isNestedGrid = selection.arrayPath !== mainSelection.arrayPath &&
        selection.arrayPath.startsWith(mainSelection.arrayPath + '.');
      effectiveArrayPath = selection.arrayPath;
      effectiveIndex = selection.index;
      effectiveItem = selection.item;
    }

    if (effectiveIndex === -1) {
      return;
    }

    const now = Date.now();
    if (this.lastFormWrite && (now - this.lastFormWrite.time) < DataSyncService.LAST_WRITE_SUPPRESS_MS) {
      if (this.lastFormWrite.value === value &&
          (this.lastFormWrite.effPath !== effectiveArrayPath || this.lastFormWrite.effIdx !== effectiveIndex)) {
        return;
      }
      // Mesmo campo/linha/valor em sequência (ex: re-fill disparando valueChanges) → não gravar de novo
      if (this.lastFormWrite.effPath === effectiveArrayPath &&
          this.lastFormWrite.effIdx === effectiveIndex &&
          this.lastFormWrite.fieldPath === fieldPath &&
          this.lastFormWrite.value === value) {
        return;
      }
    }

    if (isNestedGrid) {
      const nestedPath = effectiveArrayPath.replace(mainSelection.arrayPath + '.', '');
      const fullArrayPath = `${mainSelection.arrayPath}[${mainSelection.index}].${nestedPath}`;
      const fullPath = `${fullArrayPath}[${effectiveIndex}].${fieldPath}`;
      this.dataBindingService.updateValue(fullPath, value);
    } else {
      this.dataBindingService.updateArrayItemProperty(
        effectiveArrayPath,
        effectiveIndex,
        fieldPath,
        value
      );
    }

    this.lastFormWrite = { value, effPath: effectiveArrayPath, effIdx: effectiveIndex, fieldPath, time: Date.now() };

    const updatedItem = { ...effectiveItem };
    this.setNestedValue(updatedItem, fieldPath, value);

    // CORREÇÃO: NÃO emitir mainSelectionSubject quando apenas o item é atualizado
    // Isso evita cascata desnecessária nas grades filhas
    // mainSelectionSubject deve ser emitido APENAS quando o ÍNDICE muda (mudança de linha)
    // currentSelectionSubject é suficiente para atualizar formulários
    this.currentSelectionSubject.next({
      item: updatedItem,
      index: effectiveIndex,
      arrayPath: effectiveArrayPath
    });
    
    // NOTA: Removido mainSelectionSubject.next() aqui pois causava cascata em grades filhas
    // As grades filhas escutam mainSelectionSubject para detectar mudança de linha do pai
    // Se emitirmos mainSelectionSubject aqui (mesmo com mesmo índice), pode haver race conditions
  }
  
  /**
   * Define um valor em um objeto aninhado
   */
  private setNestedValue(obj: any, path: string, value: any): void {
    const parts = path.split('.');
    let current = obj;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    current[parts[parts.length - 1]] = value;
  }
  
  /**
   * ⭐ Obtém o item atualmente selecionado
   */
  getCurrentItem(): any | null {
    return this.currentSelectionSubject.value.item;
  }
  
  /**
   * ⭐ Obtém o índice do item atualmente selecionado
   */
  getCurrentIndex(): number {
    return this.currentSelectionSubject.value.index;
  }
  
  /**
   * ⭐ Obtém a seleção da grade principal (pai) - usado em master-detail
   */
  getMainSelection(): { item: any | null; index: number; arrayPath: string } {
    return this.mainSelectionSubject.value;
  }

  /**
   * ⭐ Obtém a seleção atual (pode ser grade pai ou filha) - reflete a linha que o formulário está exibindo
   */
  getCurrentSelection(): { item: any | null; index: number; arrayPath: string } {
    return this.currentSelectionSubject.value;
  }
  
  /**
   * ⭐ Obtém a linha PAI completa a partir do store (inclui arrays aninhados: channels, etc.).
   * Deve ser usada no fluxo "selectedItem" para enviar sempre o registro pai com todos os filhos.
   */
  getMainRowFromStore(): any | null {
    const main = this.mainSelectionSubject.value;
    if (main.index === -1 || !main.arrayPath) return null;
    const data = this.dataBindingService.getCurrentData();
    if (!data) return null;
    const arr = this.dataBindingService.resolvePath(data, main.arrayPath);
    if (!Array.isArray(arr) || !arr[main.index]) return null;
    return arr[main.index];
  }
  
  /**
   * ⭐ Obtém o JSON completo atualizado (para envio)
   */
  getCompleteData(): any {
    return this.dataBindingService.getCurrentData();
  }
  
  /**
   * ⭐ Obtém o JSON formatado (para logs)
   */
  getFormattedData(): string {
    return this.dataBindingService.getFormattedJson();
  }
  
  /**
   * ⭐ Registra mudança de valor de campo de formulário
   * @param fieldDataPath dataPath do campo (ex: "item_name")
   * @param value Novo valor
   * @param formContextArrayPath Path do array da grade a que o formulário pertence (ex: "response.item_list"). Define se a edição é na grade pai ou filha.
   */
  onFormFieldChange(fieldDataPath: string, value: any, formContextArrayPath?: string): void {
    // CORREÇÃO: Se isCellEditing está true mas a edição vem do formulário,
    // limpar a flag para permitir que a grade atualize
    const wasCellEditing = this._isCellEditing;
    if (wasCellEditing) {
      this._isCellEditing = false;
    }
    
    // CORREÇÃO: Setar flag apenas para o path específico (evita afetar outras grids)
    const effectivePath = formContextArrayPath || this.currentSelectionSubject.value.arrayPath || '';
    this._updatingFromFormPaths.set(effectivePath, Date.now());
    try {
      this.updateCurrentItemField(fieldDataPath, value, formContextArrayPath);
    } finally {
      // FASE 5: Usar RxTimingService para limpeza com cancelamento automático
      if (this.rxTiming) {
        this.rxTiming.runAfterDelay(
          () => this._updatingFromFormPaths.delete(effectivePath),
          300,
          `clearFormPath_${effectivePath}`
        ).pipe(take(1)).subscribe();
      } else {
        // Fallback para setTimeout se RxTimingService não estiver disponível
        setTimeout(() => {
          this._updatingFromFormPaths.delete(effectivePath);
        }, 300);
      }
    }
  }
  
  /**
   * ⭐ Registra mudança de valor em célula de grid
   * @param rowIndex Índice da linha DENTRO DO ARRAY da grid
   * @param columnDataPath dataPath da coluna (relativo ao item da grid)
   * @param value Novo valor
   * @param gridDataPath Path completo do array da grid (ex: 'response.item_list' ou 'response.item_list.image.image_id_list')
   */
  /**
   * Mudança vinda da GRADE: não setamos isUpdatingFromForm, para que o canvas
   * possa chamar updateFormFieldsFromGlobalData quando getData$() emitir e o formulário atualize.
   */
  onGridCellChange(rowIndex: number, columnDataPath: string, value: any, gridDataPath?: string): void {
    const arrayPath = gridDataPath || 'response.item_list';

    if (!arrayPath) {
      return;
    }

    // NOTA: A flag _isCellEditing agora é controlada pelos eventos focus/blur
    // dos inputs da grade (onCellFocus/onCellBlur)

    if (arrayPath.split('.').length > 2) {
      const parentSelection = this.mainSelectionSubject.value;
      
      if (parentSelection.index === -1) {
        return;
      }
      
      // Construir path completo: response.magalu[X].channels[Y].extras.myName
      // onde X é o índice do item pai (mainSelection), Y é o rowIndex da grade filha
      
      // Extrair apenas a parte do path que vem DEPOIS do parentArrayPath
      // Ex: arrayPath = "response.magalu.channels", parentArrayPath = "response.magalu"
      // → nestedPath = "channels"
      const nestedPath = arrayPath.replace(parentSelection.arrayPath + '.', '');
      
      // Atualizar via path aninhado completo
      const fullPath = `${parentSelection.arrayPath}[${parentSelection.index}].${nestedPath}[${rowIndex}].${columnDataPath}`;
      this.dataBindingService.updateValue(fullPath, value);
      
    } else {
      // Grid principal - usar método padrão
      this.dataBindingService.updateArrayItemProperty(
        arrayPath,
        rowIndex,
        columnDataPath,
        value
      );
    }
    
    // CORREÇÃO: Atualizar currentSelectionSubject APENAS quando NÃO estiver editando célula.
    // Durante edição de célula, a sincronização é feita via fieldSync$ (apenas o campo editado).
    // Emitir currentSelectionSubject durante edição causava re-fill dos formulários com dados
    // potencialmente incompletos, apagando outros campos.
    if (!this._isCellEditing) {
      const currentSelection = this.currentSelectionSubject.value;
      const data = this.dataBindingService.getCurrentData();
      
      if (data && currentSelection.index === rowIndex && currentSelection.arrayPath === arrayPath) {
        // Re-emitir com o item atualizado do store
        let updatedArray: any[];
        
        if (arrayPath.split('.').length > 2) {
          // Grade aninhada - resolver path completo
          const parentSelection = this.mainSelectionSubject.value;
          const nestedPath = arrayPath.replace(parentSelection.arrayPath + '.', '');
          const fullArrayPath = `${parentSelection.arrayPath}[${parentSelection.index}].${nestedPath}`;
          updatedArray = this.dataBindingService.resolvePath(data, fullArrayPath);
        } else {
          // Grade principal
          updatedArray = this.dataBindingService.resolvePath(data, arrayPath);
        }
        
        if (Array.isArray(updatedArray) && updatedArray[rowIndex]) {
          this.currentSelectionSubject.next({
            ...currentSelection,
            item: updatedArray[rowIndex]
          });
        }
      }
    }
    
    // Também atualizar mainSelection se for a grade principal
    // CORREÇÃO: NÃO emitir mainSelectionSubject durante edição de célula!
    // Isso estava causando cascata de eventos em grades filhas.
    // O mainSelection já tem a referência correta, não precisa re-emitir.
    // A atualização do item é feita in-place pelo DataBindingService.
    
    // NOTA: A flag _isCellEditing agora é controlada pelos eventos focus/blur
    // dos inputs da grade (onCellFocus/onCellBlur), não mais por setTimeout aqui.
  }
  
  /**
   * ⭐ NOVO: Adiciona uma nova linha na grid
   * @param gridDataPath Path do array da grid (ex: 'response.item_list')
   * @param newItem Item opcional (se não fornecido, cria template vazio)
   * @param columnConfig Configuração das colunas (para criar item em array vazio)
   * @param gridId ID da grid (para setSelectedRow ao adicionar na grid principal)
   * @param targetPanelId Painel de destino (legacy)
   * @param targetPanelIds Painéis de destino (para preencher formulário ao adicionar linha)
   */
  addGridRow(
    gridDataPath: string,
    newItem?: any,
    columnConfig?: any[],
    gridId?: string,
    targetPanelId?: string,
    targetPanelIds?: string[],
    defaultValues?: Record<string, any>,
    isInitialAutoAdd: boolean = false
  ): void {
    if (isInitialAutoAdd && gridId) {
      this.pendingInitialAutoAddGridIds.add(gridId);
    }

    const pathParts = gridDataPath.split('.');
    const isNestedGrid = pathParts.length > 2;
    
    if (isNestedGrid) {
      // Grid aninhada - precisa incluir índice do item pai
      const parentSelection = this.mainSelectionSubject.value;
      
      if (parentSelection.index === -1) {
        return;
      }
      
      const parentArrayPath = parentSelection.arrayPath;
      let nestedPath = gridDataPath;
      if (gridDataPath.startsWith(parentArrayPath + '.')) {
        nestedPath = gridDataPath.substring(parentArrayPath.length + 1);
      }
      
      const fullPath = `${parentArrayPath}[${parentSelection.index}].${nestedPath}`;
      this.dataBindingService.addArrayItem(fullPath, newItem, columnConfig, defaultValues);
      
      // Re-disparar evento de seleção do item PAI atualizado após adicionar linha aninhada.
      // A seleção da NOVA linha na grade filha é feita pelo próprio DataGridComponent
      // em loadNestedData() (detecção de rowAdded: array cresceu +1 sem troca de pai).
      const selectAfterAdd = () => {
        this.dataBindingService.getData$().pipe(take(1)).subscribe(currentData => {
          const parentArray = this.dataBindingService.resolvePath(currentData, parentArrayPath);
          if (Array.isArray(parentArray) && parentArray[parentSelection.index]) {
            const updatedParentItem = parentArray[parentSelection.index];
            
            // Re-disparar evento de seleção com item PAI atualizado
            this.masterDetailService.setSelectedRow(
              'grid',
              updatedParentItem,
              undefined,
              false,
              parentArrayPath,
              parentSelection.index
            );
          }
        });
      };
      
      if (this.rxTiming) {
        this.rxTiming.runAfterDelay(selectAfterAdd, 50, 'selectAfterNestedAdd')
          .pipe(take(1)).subscribe();
      } else {
        setTimeout(selectAfterAdd, 50);
      }
    } else {
      this.dataBindingService.addArrayItem(gridDataPath, newItem, columnConfig, defaultValues);
      
      // FASE 5: Usar RxTimingService para seleção após adicionar
      const selectNewRow = () => {
        const currentData = this.dataBindingService.getCurrentData();
        
        // Precisa resolver o array corretamente (pode ter sido criado em objeto.item_list)
        let resolvedPath = gridDataPath;
        let array = this.dataBindingService.resolvePath(currentData, gridDataPath);
        
        // Se não é array, procurar array dentro do objeto
        if (!Array.isArray(array) && array && typeof array === 'object') {
          const firstArrayKey = this.findFirstArrayKey(array);
          if (firstArrayKey) {
            resolvedPath = `${gridDataPath}.${firstArrayKey}`;
            array = array[firstArrayKey];
          }
        }
        
        if (Array.isArray(array) && array.length > 0) {
          // Selecionar a última linha (recém-criada)
          const newIndex = array.length - 1;
          const newRowItem = array[newIndex];
          
          // Atualizar AMBOS mainSelection e currentSelection
          // mainSelection é usado para hierarquia de grades
          this.mainSelectionSubject.next({
            item: newRowItem,
            index: newIndex,
            arrayPath: resolvedPath
          });
          
          this.currentSelectionSubject.next({
            item: newRowItem,
            index: newIndex,
            arrayPath: resolvedPath
          });

          // Preencher formulário ao adicionar linha na grid principal
          const panelIds = targetPanelIds || (targetPanelId ? [targetPanelId] : []);
          if (panelIds.length > 0) {
            this.masterDetailService.setSelectedRow(
              gridId || 'grid',
              newRowItem,
              targetPanelId,
              false,
              resolvedPath,
              newIndex,
              panelIds
            );
          }
        }
      };
      
      if (this.rxTiming) {
        this.rxTiming.runAfterDelay(selectNewRow, 150, 'selectAfterMainAdd')
          .pipe(take(1)).subscribe();
      } else {
        setTimeout(selectNewRow, 150);
      }
    }
  }

  /**
   * Consome (read-once) a marca de auto-add inicial para uma grade.
   */
  consumeInitialAutoAdd(gridId?: string): boolean {
    if (!gridId) return false;
    if (!this.pendingInitialAutoAddGridIds.has(gridId)) return false;
    this.pendingInitialAutoAddGridIds.delete(gridId);
    return true;
  }
  
  /**
   * Encontra a primeira chave que contém um array dentro de um objeto
   */
  private findFirstArrayKey(obj: any): string | null {
    if (!obj || typeof obj !== 'object') return null;
    
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        return key;
      }
    }
    
    return null;
  }
  
  /**
   * ⭐ NOVO: Remove uma linha da grid
   * @param gridDataPath Path do array da grid
   * @param rowIndex Índice da linha a ser removida
   */
  removeGridRow(gridDataPath: string, rowIndex: number): void {
    // Verificar se é grid aninhada
    const pathParts = gridDataPath.split('.');
    
    if (pathParts.length > 2) {
      // Grid aninhada
      const parentSelection = this.mainSelectionSubject.value;
      
      if (parentSelection.index === -1) {
        return;
      }
      
      const parentArrayPath = parentSelection.arrayPath;
      let nestedPath = gridDataPath;
      if (gridDataPath.startsWith(parentArrayPath + '.')) {
        nestedPath = gridDataPath.substring(parentArrayPath.length + 1);
      }
      
      const fullPath = `${parentArrayPath}[${parentSelection.index}].${nestedPath}`;
      this.dataBindingService.removeArrayItem(fullPath, rowIndex);
    } else {
      this.dataBindingService.removeArrayItem(gridDataPath, rowIndex);
    }
  }
}

